// Phase J — Self-defending APIs Proposals — v2 MUI (LDCP-160).
//
// Three tabs: Pending / History / Settings.
//
// Backend wiring preserved verbatim:
//   asec.proposalsList(siteId)
//   asec.proposalPatch(siteId, id, { action, reason })
//      action: "accept" | "reject" | "defer" | "reopen"

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Link,
} from "@mui/material";
import {
  Cpu, Inbox, History, Settings2, Target, CheckCircle2, XCircle,
  Check, X, CheckCheck, Clock, Activity, Gauge, Table2, UserX, ShieldX,
  MapPin, Bot, ArrowUpRight, KeyRound, Radar, Zap, Bell, Timer,
  GitMerge,
} from "lucide-react";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import { GreenSwitch } from "../../../../common/Greens";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecTabsSx, asecContentCardSx, asecHelperTextSx, ASEC_PRIMARY_TAG,
} from "./asecUi";
import {
  AsecMetricStrip,
  AsecSectionCard,
  AsecMono,
  AsecStatusPill,
  AsecHeroStatValue,
} from "./components/asecPrimitives";
import ProposalEvidenceDrawer from "./components/ProposalEvidenceDrawer";
import ProposalActionButtons from "./components/ProposalActionButtons";
import StickySaveBar from "../../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";

const TAB_KEYS = ["pending", "history", "settings"];

const DEFAULT_PROPOSER_SETTINGS = {
  daemonOn: true,
  autoAccept: false,
  notify: true,
  rejectBackoff: true,
};

const PROPOSER_SETTINGS_DIFF_ENTRIES = [
  { key: "daemonOn", label: "Enable proposer daemon", format: enabledLabel },
  { key: "autoAccept", label: "Auto-accept high-confidence ATO proposals", format: enabledLabel },
  { key: "notify", label: "Notify on new proposals", format: enabledLabel },
  { key: "rejectBackoff", label: "Reject backoff", format: enabledLabel },
];

function isWithinDays(ts, days) {
  if (!ts) return false;
  return Date.now() - new Date(ts).getTime() <= days * 86400 * 1000;
}

function categoriseProposal(p) {
  const tt = (p.target_table || "").toLowerCase();
  const reason = (p.reason || "").toLowerCase();
  if (tt.includes("authz")) return "authz";
  if (tt.includes("authn") && (reason.includes("geo") || reason.includes("travel") || reason.includes("step-up"))) return "geo";
  if (tt.includes("authn")) return "geo";
  if (reason.includes("ato") || reason.includes("takeover") || reason.includes("credential")) return "ato";
  if (reason.includes("scrap") || reason.includes("bot") || reason.includes("crawler")) return "bot";
  if (tt.includes("rate") || reason.includes("burst") || reason.includes("rate")) return "rate";
  return "rate";
}

function categoryStyle(cat) {
  return cat === "ato"   ? { bg: colors.red[10], fg: colors.red[200], Icon: UserX }
       : cat === "authz" ? { bg: colors.primary[10], fg: colors.primary[300], Icon: ShieldX }
       : cat === "geo"   ? { bg: "#F3EEFE", fg: "#6B3FA0", Icon: MapPin }
       : cat === "bot"   ? { bg: colors.green[10], fg: colors.green[300], Icon: Bot }
       : { bg: "#FFF1E6", fg: "#B25A1A", Icon: Gauge };
}

function shortTitle(p) {
  const r = (p.reason || "").trim();
  if (!r) return "Policy proposal";
  const period = r.indexOf(".");
  const dash = r.indexOf(" — ");
  const cut = (period > 0 && period < 80) ? period
            : (dash > 0 && dash < 80) ? dash
            : Math.min(r.length, 80);
  return r.slice(0, cut);
}

function confidenceFromHits(n) {
  if (n == null) return null;
  if (n >= 10) return "high";
  if (n >= 3) return "medium";
  return "low";
}

function ruleSnippet(p) {
  const cat = categoriseProposal(p);
  const tt = (p.target_table || "").replace("api_security.", "");
  const ttClean = tt || "rule";
  const reason = p.reason || "";
  const idMatch = reason.match(/(jwt:[\w.-]+|sub_[\w-]+)/i);
  const routeMatch = reason.match(/\/[\w\/{}.-]+/);
  if (cat === "ato" || (tt.includes("rate") && idMatch)) {
    return [
      { k: "match", v: `identity = ${idMatch ? `"${idMatch[0]}"` : '"…"'}` },
      { k: "limit", v: "20 req / 60s then challenge", hl: true },
      { k: "scope", v: routeMatch ? routeMatch[0] : "/v1/auth/*" },
    ];
  }
  if (cat === "authz") {
    return [
      { k: "match", v: `subject = ${idMatch ? `"${idMatch[0]}"` : '"…"'} AND route = "${routeMatch ? routeMatch[0] : "/v1/…"}"` },
      { k: "action", v: "deny 403", hl: true },
      { k: "ttl", v: "7d" },
    ];
  }
  if (cat === "geo") {
    return [
      { k: "match", v: `route = "${routeMatch ? routeMatch[0] : "/admin/login"}" AND geo_velocity > 800km/h`, hl: true },
      { k: "action", v: "require step-up (mfa)" },
    ];
  }
  if (cat === "bot") {
    return [
      { k: "match", v: `asn_type = "datacenter" AND route = "${routeMatch ? routeMatch[0] : "/v1/products"}"` },
      { k: "limit", v: "10 req / 10s then challenge", hl: true },
    ];
  }
  return [
    { k: "match", v: routeMatch ? `route = "${routeMatch[0]}"` : "(see reason)" },
    { k: "target", v: ttClean, hl: true },
  ];
}

function StateBadge({ state, variant = "history" }) {
  const conf = variant === "pending"
    ? (state === "draft"
      ? { kind: "indigo", Icon: Clock, label: "Pending" }
      : state === "reviewed"
        ? { kind: "info", Icon: Clock, label: "Deferred" }
        : { kind: "default", Icon: Activity, label: state })
    : (state === "accepted" ? { kind: "ok", Icon: Check, label: state }
      : state === "rejected" ? { kind: "danger", Icon: X, label: state }
      : state === "deferred" || state === "reviewed" ? { kind: "info", Icon: Clock, label: state }
      : state === "draft" ? { kind: "indigo", Icon: Clock, label: "Pending" }
      : { kind: "default", Icon: Activity, label: state });
  return (
    <AsecStatusPill kind={conf.kind} sx={{ fontWeight: 700, height: 24, fontSize: 12 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} component="span">
        <LayerdIcon icon={conf.Icon} size={12} color="inherit" />
        {conf.label}
      </Stack>
    </AsecStatusPill>
  );
}

function TblChip({ children }) {
  return (
    <AsecMono sx={{
      backgroundColor: ASEC_PRIMARY_TAG.bg,
      color: ASEC_PRIMARY_TAG.fg,
      border: `1px solid ${ASEC_PRIMARY_TAG.bd}`,
      fontWeight: 500,
      px: 0.875,
      py: 0.25,
      borderRadius: "4px",
    }}>
      {children}
    </AsecMono>
  );
}

function renderReason(text) {
  if (!text) return <Typography component="span" sx={{ color: colors.neutral[500] }}>No reason recorded.</Typography>;
  const re = /(jwt:[\w.-]+|sub_[\w-]+|\/[\w\/{}.-]+|\b\d+\s+(?:access_burst|ownership_mismatch|req\/s|recent|hits|min|sec|days?|hr)\b[\w\s]*?(?=\s|\.|,|$))/gi;
  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (/\baccess_burst|ownership_mismatch|req\/s/.test(tok)) {
      parts.push(<Box key={parts.length} component="span" sx={{ color: colors.red[200], fontWeight: 600 }}>{tok}</Box>);
    } else if (tok.startsWith("/") || tok.startsWith("jwt:") || tok.startsWith("sub_")) {
      parts.push(<AsecMono key={parts.length}>{tok}</AsecMono>);
    } else {
      parts.push(tok);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function HeroMetrics({ rows }) {
  const pending = rows.filter((r) => r.state === "draft" || r.state === "reviewed").length;
  const accepted7d = rows.filter((r) => r.state === "accepted" && isWithinDays(r.resolved_at || r.created_at, 7)).length;
  const rejected7d = rows.filter((r) => r.state === "rejected" && isWithinDays(r.resolved_at || r.created_at, 7)).length;
  const acc30 = rows.filter((r) => r.state === "accepted" && isWithinDays(r.resolved_at || r.created_at, 30)).length;
  const rej30 = rows.filter((r) => r.state === "rejected" && isWithinDays(r.resolved_at || r.created_at, 30)).length;
  const rate = (acc30 + rej30) > 0 ? Math.round(acc30 / (acc30 + rej30) * 100) : null;

  return (
    <AsecMetricStrip metrics={[
      {
        key: "daemon",
        label: "Proposer daemon",
        icon: Cpu,
        highlight: true,
        value: (
          <AsecHeroStatValue
            primary="Running"
            pill={(
              <AsecStatusPill kind="indigo">
                <Stack direction="row" alignItems="center" spacing={0.75} component="span">
                  <Box sx={{
                    width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor",
                    animation: "ldPulse 1.6s ease-in-out infinite",
                    "@keyframes ldPulse": { "0%,100%": { opacity: 0.5 }, "50%": { opacity: 1 } },
                  }} />
                  healthy
                </Stack>
              </AsecStatusPill>
            )}
          />
        ),
        sub: "mines auth + anomaly findings every 5 min",
      },
      {
        key: "pending",
        label: "Pending",
        icon: Inbox,
        value: pending,
        valueSx: { color: pending > 0 ? "#C29021" : undefined },
        sub: pending > 0 ? "awaiting your decision" : "nothing waiting",
      },
      {
        key: "accepted",
        label: "Accepted · 7d",
        icon: CheckCircle2,
        value: accepted7d,
        valueSx: { color: colors.green[300] },
        sub: "promoted to live rules",
      },
      {
        key: "rejected",
        label: "Rejected · 7d",
        icon: XCircle,
        value: rejected7d,
        sub: "30-day re-propose backoff",
      },
      {
        key: "rate",
        label: "Acceptance rate",
        icon: Target,
        value: rate != null ? `${rate}%` : "—",
        sub: "last 30 days",
      },
    ]} />
  );
}

function MetaItem({ icon: Icon, children }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px" }}>
      <LayerdIcon icon={Icon} size={14} color="neutral" />
      {children}
    </Stack>
  );
}

function ProposalCard({ p, onAct, canConfigure, busy }) {
  const cat = categoriseProposal(p);
  const cs = categoryStyle(cat);
  const isResolved = p.state === "accepted" || p.state === "rejected" || p.state === "deferred";
  const conf = confidenceFromHits(p.recent_hit_count);
  const tt = (p.target_table || "rule").replace("api_security.", "");
  const snippet = ruleSnippet(p);
  const reason = (p.reason || "").trim();
  const title = shortTitle(p);
  const showFullReason = reason.length > title.length + 2;

  const actions = (
    <ProposalActionButtons
      proposal={p}
      onAct={onAct}
      canConfigure={canConfigure}
      busy={busy}
      sx={{ pr: 0.5 }}
    />
  );

  return (
    <Box sx={{
      ...asecContentCardSx,
      opacity: isResolved ? 0.72 : 1,
      transition: "border-color 160ms, box-shadow 160ms",
      "&:hover": isResolved ? {} : {
        borderColor: colors.neutral[300],
        boxShadow: "0 4px 16px rgba(17,17,17,0.05)",
      },
    }}>
      <Box sx={{ p: 2.25 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", display: "flex",
              alignItems: "center", justifyContent: "center", flexShrink: 0,
              backgroundColor: cs.bg, color: cs.fg,
            }}>
              <LayerdIcon icon={cs.Icon} size={18} color="inherit" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: showFullReason ? 0.5 : 0.75 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: "21px", color: colors.neutral[1000] }}>
                  {renderReason(title)}
                </Typography>
                <StateBadge state={p.state} variant="pending" />
              </Stack>
              {showFullReason && (
                <Typography sx={{ fontSize: 12, color: colors.neutral[700], ...asecHelperTextSx, mb: 0.75 }}>
                  {renderReason(reason)}
                </Typography>
              )}
              <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: showFullReason ? 0.5 : 0 }}>
                <MetaItem icon={Table2}>
                  Target <TblChip>{tt}</TblChip>
                </MetaItem>
                <MetaItem icon={Activity}>
                  <Box component="span" sx={{ fontWeight: 600, color: colors.neutral[1000] }}>
                    {p.recent_hit_count != null ? p.recent_hit_count : "—"}
                  </Box>
                  {" "}recent hit{p.recent_hit_count === 1 ? "" : "s"}
                </MetaItem>
                <MetaItem icon={Clock}>
                  Created {p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}
                </MetaItem>
                {conf && (
                  <MetaItem icon={Gauge}>
                    Confidence{" "}
                    <Box component="span" sx={{ fontWeight: 600, color: colors.neutral[1000] }}>{conf}</Box>
                  </MetaItem>
                )}
                {p.promoted_rule_id && (
                  <MetaItem icon={CheckCircle2}>
                    <Box component="span" sx={{ color: "#0f7a42", fontWeight: 600 }}>
                      Promoted <AsecMono>{p.promoted_rule_id.slice(0, 8)}…</AsecMono>
                    </Box>
                  </MetaItem>
                )}
              </Stack>
            </Box>
          </Stack>
          <Box sx={{ display: { xs: "none", md: "block" } }}>{actions}</Box>
        </Stack>
        <Box sx={{ display: { xs: "block", md: "none" }, mt: 1.5 }}>{actions}</Box>
      </Box>

      <Box sx={{ borderTop: `1px solid ${colors.neutral[200]}`, backgroundColor: colors.neutral[50] }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.25,
            py: 1.25,
            borderBottom: `1px solid ${colors.neutral[200]}`,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: colors.neutral[600],
          }}
        >
          <span>Proposed rule</span>
          <Link
            component="button"
            underline="none"
            onClick={() => onAct(p, "evidence")}
            sx={{
              color: colors.primary[300],
              fontSize: 12,
              fontWeight: 500,
              lineHeight: "20px",
              textTransform: "none",
              letterSpacing: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            View evidence
            <LayerdIcon icon={ArrowUpRight} size={12} color="primaryLight" />
          </Link>
        </Stack>
        <Box
          component="pre"
          sx={{
            m: 0,
            px: 2.25,
            py: 1.75,
            backgroundColor: "#fff",
            fontSize: 12,
            lineHeight: "25px",
            whiteSpace: "pre-wrap",
            fontFamily: "inherit",
          }}
        >
          {snippet.map((row, i) => (
            <Box key={i} component="span" sx={{ display: "block" }}>
              <Box component="span" sx={{ color: colors.primary[300], fontWeight: 600 }}>{row.k}</Box>
              {" "}
              <Box
                component="span"
                sx={{
                  color: row.hl ? colors.primary[500] : colors.neutral[1000],
                  fontWeight: row.hl ? 600 : 400,
                }}
              >
                {row.v}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function PendingTab({ rows, onAct, canConfigure, busy }) {
  if (rows.length === 0) {
    return (
      <Stack alignItems="center" spacing={1.25} sx={{ py: 6, px: 2 }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: "13px", backgroundColor: colors.green[10],
          color: colors.green[300], display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <LayerdIcon icon={CheckCheck} size="lg" color="inherit" />
        </Box>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>All caught up</Typography>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700], maxWidth: 420, textAlign: "center", ...asecHelperTextSx }}>
          No pending proposals. The daemon re-mines your auth and anomaly findings every 5 minutes — new drafts will appear here.
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={1.75}>
      {rows.map((p) => (
        <ProposalCard key={p.id} p={p} onAct={onAct} canConfigure={canConfigure} busy={busy} />
      ))}
    </Stack>
  );
}

function HistoryTab({ rows }) {
  return (
    <AsecSectionCard
      title="Decision history"
      icon={History}
      radius="12px"
      noPadding
      bodySx={{ p: 0 }}
      right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>last 30 days · {rows.length} resolved</Typography>}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
              <TableCell sx={{ fontSize: 14, fontWeight: 500, width: 120 }}>State</TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 500 }}>Reason</TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 500, width: 160 }}>Target table</TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 500, width: 100 }} align="right">Hits</TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 500, width: 150 }}>Resolved</TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 500, width: 120 }}>By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.neutral[700] }}>
                  No resolved proposals yet.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell><StateBadge state={r.state} /></TableCell>
                <TableCell sx={{ fontSize: 14, lineHeight: "19px" }}>{renderReason(r.reason)}</TableCell>
                <TableCell><TblChip>{(r.target_table || "").replace("api_security.", "") || "—"}</TblChip></TableCell>
                <TableCell align="right">{r.recent_hit_count != null ? r.recent_hit_count : "—"}</TableCell>
                <TableCell sx={{ fontSize: 12, color: colors.neutral[700] }}>
                  {r.resolved_at ? new Date(r.resolved_at).toLocaleDateString()
                                 : (r.created_at ? new Date(r.created_at).toLocaleDateString() : "—")}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.resolved_by || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </AsecSectionCard>
  );
}

function ToggleRow({ icon: Icon, name, desc, on, onChange, disabled, pill, last }) {
  return (
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}
      sx={{ py: 1.75, borderBottom: last ? 0 : `1px solid rgba(0,0,0,0.08)` }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ fontSize: 14, fontWeight: 600, lineHeight: "25px", mb: 0.375 }}>
          <LayerdIcon icon={Icon} size="sm" color="neutral" />
          {name}
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px", maxWidth: 560 }}>{desc}</Typography>
      </Box>
      {pill || <GreenSwitch checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />}
    </Stack>
  );
}

function SettingsTab({
  daemonOn, setDaemonOn, autoAccept, setAutoAccept, notify, setNotify,
  rejectBackoff, setRejectBackoff, sources, canConfigure, saving,
}) {
  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="flex-start">
      <AsecSectionCard title="Proposer settings" icon={Settings2} radius="12px" sx={{ flex: 1 }} bodySx={{ px: 2.25, py: 0.75 }}>
        <ToggleRow icon={Cpu} name="Enable proposer daemon"
          desc="Mine auth events + anomaly findings every 5 minutes and draft per-site policy proposals."
          on={daemonOn} disabled={!canConfigure || saving} onChange={setDaemonOn} />
        <ToggleRow icon={Zap} name="Auto-accept high-confidence ATO proposals"
          desc={<>Immediately promote account-takeover rate caps flagged <AsecMono>high</AsecMono> confidence. Notifies on action. (Site-local preference — not wired to the proposer daemon yet.)</>}
          on={autoAccept} disabled={!canConfigure || saving} onChange={setAutoAccept} />
        <ToggleRow icon={Bell} name="Notify on new proposals"
          desc="Slack + email when a new draft is created. (Routes through the alert-rules system once configured.)"
          on={notify} disabled={!canConfigure || saving} onChange={setNotify} />
        <ToggleRow icon={Timer} name="Reject backoff"
          desc={<>Rejected proposals are not re-proposed for <AsecMono>30</AsecMono> days.</>}
          on={rejectBackoff} disabled={!canConfigure || saving} onChange={setRejectBackoff} last />
      </AsecSectionCard>
      <AsecSectionCard title="Sources mined" icon={GitMerge} radius="12px" sx={{ flex: 1 }} bodySx={{ px: 2.25, py: 0.75 }}>
        {sources.map((s, i) => (
          <ToggleRow key={s.key} icon={s.Icon} name={s.name} desc={s.desc}
            pill={s.on ? <AsecStatusPill kind="ok">on</AsecStatusPill> : <AsecStatusPill>off</AsecStatusPill>}
            last={i === sources.length - 1} />
        ))}
      </AsecSectionCard>
    </Stack>
  );
}

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const { canConfigure } = useAsecPermissions();
  const [tab, setTab] = React.useState("pending");
  const [rows, setRows] = React.useState([]);
  const [busy, setBusy] = React.useState(null);
  const [daemonOn, setDaemonOn] = React.useState(DEFAULT_PROPOSER_SETTINGS.daemonOn);
  const [autoAccept, setAutoAccept] = React.useState(DEFAULT_PROPOSER_SETTINGS.autoAccept);
  const [notify, setNotify] = React.useState(DEFAULT_PROPOSER_SETTINGS.notify);
  const [rejectBackoff, setRejectBackoff] = React.useState(DEFAULT_PROPOSER_SETTINGS.rejectBackoff);
  const [savedSettings, setSavedSettings] = React.useState(DEFAULT_PROPOSER_SETTINGS);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const [evidenceProposal, setEvidenceProposal] = React.useState(null);

  const settingsDraft = React.useMemo(
    () => ({ daemonOn, autoAccept, notify, rejectBackoff }),
    [daemonOn, autoAccept, notify, rejectBackoff],
  );

  const settingsModifiedFields = React.useMemo(
    () => collectObjectDiff(savedSettings, settingsDraft, PROPOSER_SETTINGS_DIFF_ENTRIES),
    [savedSettings, settingsDraft],
  );
  const settingsModifiedCount = settingsModifiedFields.length;

  const onDiscardSettings = React.useCallback(() => {
    setDaemonOn(savedSettings.daemonOn);
    setAutoAccept(savedSettings.autoAccept);
    setNotify(savedSettings.notify);
    setRejectBackoff(savedSettings.rejectBackoff);
  }, [savedSettings]);

  const onApplySettings = React.useCallback(async () => {
    if (!canConfigure || settingsModifiedCount === 0) return;
    setSavingSettings(true);
    try {
      // Site-local preferences — API wiring pending; persist draft locally for now.
      setSavedSettings(settingsDraft);
      showSuccess("Proposer settings saved.");
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setSavingSettings(false);
    }
  }, [canConfigure, settingsDraft, settingsModifiedCount, showSuccess, showError]);

  const sources = React.useMemo(() => ([
    { key: "auth_events", Icon: KeyRound, name: "auth_events", desc: "Login failures, token anomalies, session replays.", on: true },
    { key: "anomaly_findings", Icon: Radar, name: "anomaly_findings", desc: "access_burst, geo-velocity, enumeration patterns.", on: true },
    { key: "authz_denials", Icon: ShieldX, name: "authz_denials", desc: "Repeated BOLA / ownership-mismatch denials.", on: true },
    { key: "bot_signals", Icon: Bot, name: "bot_signals", desc: "Datacenter ASN + rotating-UA fingerprints.", on: false },
  ]), []);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await asec.proposalsList(siteId);
      setRows((data && data.items) || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  }, [siteId, showError]);
  React.useEffect(() => { reload(); }, [reload]);

  const decide = async (p, action) => {
    if (action === "evidence") {
      setEvidenceProposal(p);
      return;
    }
    if (!canConfigure) return;
    let reason;
    if (action === "reject" || action === "defer") {
      reason = window.prompt(action === "reject" ? "Reject reason (optional)" : "Defer note (optional)") || "";
    }
    setBusy(p.id);
    try {
      await asec.proposalPatch(siteId, p.id, { action, reason });
      showSuccess(action === "accept" ? "Accepted — promoting on next tick"
                : action === "reject" ? "Rejected — 30-day backoff"
                : action === "defer"  ? "Deferred — snoozed"
                : "Reopened.");
      await reload();
      if (evidenceProposal?.id === p.id) {
        setEvidenceProposal(null);
      }
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setBusy(null);
    }
  };

  const pending = rows.filter((r) => r.state === "draft" || r.state === "reviewed");
  const history = rows.filter((r) => r.state === "accepted" || r.state === "rejected" || r.state === "deferred");
  const tabIndex = TAB_KEYS.indexOf(tab);

  return (
    <Stack spacing={2.5}>
      <HeroMetrics rows={rows} />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Tabs value={tabIndex >= 0 ? tabIndex : 0} onChange={(_, v) => setTab(TAB_KEYS[v])} sx={asecTabsSx}>
          <Tab label={`Pending (${pending.length})`} />
          <Tab label={`History (${history.length})`} />
          <Tab label="Settings" />
        </Tabs>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ display: { xs: "none", md: "flex" }, pr: 0.5 }}>
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[200] }} />
          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>Daemon healthy · live</Typography>
        </Stack>
      </Stack>

      {tab === "pending" && <PendingTab rows={pending} onAct={decide} canConfigure={canConfigure} busy={busy} />}
      {tab === "history" && <HistoryTab rows={history} />}
      {tab === "settings" && (
        <SettingsTab
          daemonOn={daemonOn} setDaemonOn={setDaemonOn}
          autoAccept={autoAccept} setAutoAccept={setAutoAccept}
          notify={notify} setNotify={setNotify}
          rejectBackoff={rejectBackoff} setRejectBackoff={setRejectBackoff}
          sources={sources}
          canConfigure={canConfigure}
          saving={savingSettings}
        />
      )}

      <StickySaveBar
        open={canConfigure && settingsModifiedCount > 0}
        modifiedCount={settingsModifiedCount}
        modifiedFields={settingsModifiedFields}
        onDiscard={onDiscardSettings}
        onApply={onApplySettings}
        isApplying={savingSettings}
      />

      <ProposalEvidenceDrawer
        open={!!evidenceProposal}
        proposal={evidenceProposal}
        onClose={() => setEvidenceProposal(null)}
        onAct={decide}
        canConfigure={canConfigure}
        busy={busy}
      />
    </Stack>
  );
}

export default function Proposals() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="Per-site policy proposals drafted by the self-defending APIs daemon. Accept = daemon promotes to the target rule table on next tick. Reject = 30-day backoff."
      title="Proposals"
      helmetTitle="API Security — Proposals"
      pageDescription={(
        <>
          The policy-proposer daemon mines this site&apos;s auth events + anomaly findings every <strong>5 min</strong>{" "}
          and drafts <strong>per-site</strong> policy proposals. <strong>Accept</strong> inserts
          the rule into its target table on the next tick; <strong>Reject</strong> applies a 30-day re-propose
          backoff; <strong>Defer</strong> snoozes it. Distinct from cross-customer managed rules and the Risk
          Score recommendations.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
    >
      {(siteId) => <Body key={refreshKey} siteId={siteId} />}
    </AsecPage>
  );
}
